/**
 * Spec 031/044 (F-R1-4) — texto de la prueba de 14 días.
 *
 * GET /account/subscription manda `trialDaysLeft` como
 * `max(0, ceil((fin - ahora) / 1 día))`. Por eso:
 *  - 0 NO es "termina hoy": es que la prueba YA terminó (el backend recorta
 *    los negativos a 0). Antes el banner decía "Tu prueba termina hoy" a una
 *    cuenta con la prueba vencida y el paywall ya puesto;
 *  - 1 puede ser "hoy más tarde" o "mañana": sin la fecha exacta no se afirma
 *    el día.
 * Si el backend publica `trialEndsAt` (fecha/hora de fin), se usa el
 * calendario real.
 */

export type TrialPhase = "ended" | "today" | "tomorrow" | "last24h" | "soon" | "mid" | "start" | "running" | "unknown";

export interface ITrialNotice {
  phase: TrialPhase;
  /** Texto del banner. */
  text: string;
  /** Texto corto para "Fin de la prueba". */
  shortText: string;
  urgent: boolean;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Días de calendario entre hoy y la fecha de fin (0 = hoy, <0 = ya pasó). */
const calendarDaysUntil = (endsAt: string, now: Date): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(endsAt);
  if (!m) return null;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;
  // Fin exacto ya pasado ⇒ terminó, aunque sea el mismo día.
  if (end.getTime() <= now.getTime() && /T\d{2}:\d{2}/.test(endsAt)) return -1;
  const endDay =
    /T\d{2}:\d{2}/.test(endsAt) ? startOfDay(end) : new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.round((endDay.getTime() - startOfDay(now).getTime()) / 86_400_000);
};

export const trialNotice = (
  daysLeft: number | null | undefined,
  trialEndsAt?: string | null,
  now: Date = new Date()
): ITrialNotice => {
  const cal = trialEndsAt ? calendarDaysUntil(trialEndsAt, now) : null;

  if (cal != null) {
    if (cal < 0)
      return {
        phase: "ended",
        text: "Tu prueba terminó. Elige un plan para reactivar Prikly.",
        shortText: "Terminó",
        urgent: true,
      };
    if (cal === 0)
      return {
        phase: "today",
        text: "Tu prueba termina hoy. Elige un plan para no perder acceso.",
        shortText: "hoy",
        urgent: true,
      };
    if (cal === 1)
      return {
        phase: "tomorrow",
        text: "Tu prueba termina mañana. Elige un plan para continuar.",
        shortText: "mañana",
        urgent: true,
      };
    return fromDays(cal);
  }

  if (daysLeft == null || Number.isNaN(Number(daysLeft)))
    return {
      phase: "unknown",
      text: "Tu prueba del Plan Estándar está activa.",
      shortText: "—",
      urgent: false,
    };
  const d = Number(daysLeft);
  if (d <= 0)
    return {
      phase: "ended",
      text: "Tu prueba terminó. Elige un plan para reactivar Prikly.",
      shortText: "Terminó",
      urgent: true,
    };
  if (d === 1)
    return {
      phase: "last24h",
      text: "Tu prueba termina en menos de un día. Elige un plan para continuar.",
      shortText: "en menos de un día",
      urgent: true,
    };
  return fromDays(d);
};

const fromDays = (d: number): ITrialNotice => {
  const short = `en ${d} días`;
  if (d <= 3)
    return { phase: "soon", text: `Faltan ${d} días de prueba. Elige tu plan.`, shortText: short, urgent: true };
  if (d >= 14)
    return {
      phase: "start",
      text: "Tu prueba de 14 días recién comienza. Explora todo el Plan Estándar.",
      shortText: short,
      urgent: false,
    };
  if (d <= 7)
    return { phase: "mid", text: `Vas a mitad de tu prueba · quedan ${d} días.`, shortText: short, urgent: false };
  return {
    phase: "running",
    text: `Tu prueba del Plan Estándar está activa · quedan ${d} días.`,
    shortText: short,
    urgent: false,
  };
};
